import { appAlert, appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useEffect, useCallback } from "react";

/**
 * What is inside a dm+d usage line.
 *
 * Usage is stored under the dm+d name and several PMR spellings collapse onto
 * it. That is deliberate - it is how a brand and its generic become one line -
 * but it means the figure on screen is the output of a matching decision the
 * user has never seen. This shows the decision, and lets it be corrected.
 *
 * Drop it under any row that has a snomed:
 *   <PmrMatchLines groupId={groupId} snomed={row.snomed}
 *                  pharmacyId={row.pharmacyId} onChanged={refresh} />
 */

const API = apiUrl("");
const post = async (path, body) => {
  const token = localStorage.getItem("authToken");
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};
const bandColour = b => b === "user" ? "#2a6a2a" : b === "auto" ? "#2a4a8a" : b === "review" ? "#7a5b00" : "#8a2020";

/* ------------------------------------------------------- remap sub-dialog */

export const RemapDialog = ({
  line,
  onCancel,
  onPick
}) => {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Seed the box with the PMR wording, since that is usually most of the
  // search the user was about to type anyway.
  useEffect(() => {
    setQuery(String(line.label || "").replace(/\(.*?\)|\[.*?\]/g, " ").trim());
  }, [line.label]);
  const search = async () => {
    setBusy(true);
    setErr("");
    try {
      setRows((await post("/pmrMatchCandidates", {
        query
      })).candidates || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return <div style={{
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.5)",
    zIndex: 4000
  }}>
      <div style={{
      background: "white",
      margin: "6% auto",
      padding: "20px",
      width: "760px",
      maxWidth: "94%",
      maxHeight: "78%",
      overflow: "auto"
    }}>
        <h3 style={{
        marginTop: 0
      }}>{line.title || "Allocate this line to the right dm+d product"}</h3>
        <p style={{
        color: "#555"
      }}>
          PMR line: <b>{line.label}</b>
          {line.packSize != null && <> (pack {line.packSize})</>}
          <br />
          Its {(line.units || 0).toLocaleString()} units go to whichever product you pick,
          for the same branches and months.
        </p>
        <p style={{
        color: "#2a6a2a",
        marginTop: "-6px"
      }}>
          The allocation is remembered. Every future upload of this wording, at any
          branch in the organisation, goes straight to the product you choose without
          being matched again.
        </p>

        <div style={{
        display: "flex",
        gap: "8px",
        marginBottom: "10px"
      }}>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
          if (e.key === "Enter") search();
        }} placeholder="Search dm+d by name" style={{
          flex: 1
        }} />
          <button onClick={search} disabled={busy || query.trim().length < 3}>
            {busy ? "Searching..." : "Search"}
          </button>
          <button onClick={onCancel}>Cancel</button>
        </div>

        {err && <div style={{
        color: "#a00",
        marginBottom: "8px"
      }}>{err}</div>}

        <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "0.9em"
      }}>
          <tbody>
            {rows.map(c => <tr key={`${c.vppid}-${c.qtval}`} style={{
            borderBottom: "1px solid #eee"
          }}>
                <td style={{
              padding: "6px 4px"
            }}>{c.vmppNm}</td>
                <td style={{
              padding: "6px 4px",
              width: "70px",
              textAlign: "right"
            }}>
                  pack {c.qtval}
                </td>
                <td style={{
              padding: "6px 4px",
              width: "80px"
            }}>
                  <button onClick={() => onPick(c)}>Use this</button>
                </td>
              </tr>)}
            {!rows.length && !busy && <tr><td style={{
              padding: "14px 4px",
              color: "#888"
            }}>
                Search dm+d to choose a product.
              </td></tr>}
          </tbody>
        </table>
      </div>
    </div>;
};

/* ------------------------------------------------------------------ main */

const PmrMatchLines = ({
  groupId,
  snomed,
  pharmacyId,
  dispensingmonth,
  onChanged
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [remapping, setRemapping] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const load = useCallback(async () => {
    if (!groupId || !snomed) return;
    setLoading(true);
    setErr("");
    try {
      setData(await post("/pmrMatches", {
        groupId,
        snomed,
        pharmacyId,
        dispensingmonth
      }));
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [groupId, snomed, pharmacyId, dispensingmonth]);
  useEffect(() => {
    load();
  }, [load]);
  const apply = async (line, vppid) => {
    const what = `Allocate "${line.label}" to the product you picked?\n\n` + `${line.units.toLocaleString()} units move across, and every future upload ` + `of this line goes there automatically.`;
    if (!(await appConfirm(what))) return;
    setBusyKey(line.key);
    try {
      const r = await post("/pmrMatchOverride", {
        groupId,
        key: line.key,
        action: "remap",
        vppid,
        pharmacyId,
        label: line.label,
        packSize: line.packSize
      });
      if (r.note) (await appAlert(r.note));
      if (r.remembered) {
        (await appAlert(`Allocated to:\n\n${r.remappedTo}\n\n` + `${r.unitsMoved.toLocaleString()} units moved. This wording will now go ` + `straight to that product on future uploads.`));
      }
      setRemapping(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      (await appAlert(e.message));
    } finally {
      setBusyKey(null);
    }
  };
  if (loading && !data) return <div style={{
    padding: "8px",
    color: "#666"
  }}>Loading matches...</div>;
  if (err) return <div style={{
    padding: "8px",
    color: "#a00"
  }}>{err}</div>;
  if (!data) return null;
  const td = {
    padding: "5px 8px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top"
  };
  return <div style={{
    background: "#fbfcff",
    border: "1px solid #e3e9f5",
    padding: "10px",
    margin: "4px 0"
  }}>
      <div style={{
      marginBottom: "6px",
      color: "#333"
    }}>
        <strong>Counted under this line</strong>
        <span style={{
        color: "#666"
      }}>
          {" "}({data.lines.length} PMR {data.lines.length === 1 ? "line" : "lines"},
          {" "}{data.totalUnits.toLocaleString()} units)
        </span>
      </div>

      {data.unattributedUnits > 0 && <div style={{
      color: "#7a5b00",
      marginBottom: "6px",
      fontSize: "0.9em"
    }}>
          The lines below are all in this figure, but {data.unattributedUnits.toLocaleString()}
          {" "}units were merged before the per-line split was recorded, so how much each one
          contributed is not known. Re-uploading those months rebuilds the split and re-enables
          the corrections.
        </div>}

      <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "0.88em"
    }}>
        <tbody>
          {data.lines.map(l => <tr key={l.key}>
              <td style={td}>
                {l.label}
                {l.packSize != null && <span style={{
              color: "#777"
            }}> (pack {l.packSize})</span>}
                {l.suppliers.length > 0 && <div style={{
              color: "#888",
              fontSize: "0.92em"
            }}>{l.suppliers.join(", ")}</div>}
                {!l.exactSplit && <div style={{
              color: "#7a5b00",
              fontSize: "0.92em"
            }}>
                    split not recorded, whole figure assumed
                  </div>}
              </td>
              <td style={{
            ...td,
            width: "90px",
            textAlign: "right"
          }}>
                {l.units == null ? <span style={{
              color: "#8C8C8C"
            }} title="Merged before the split was recorded">
                      not known
                    </span> : l.units.toLocaleString()}
              </td>
              <td style={{
            ...td,
            width: "120px",
            color: bandColour(l.band)
          }}>
                {l.band === "user" ? "set by hand" : l.band}
                {l.score != null && <span style={{
              color: "#888"
            }}> {l.score}</span>}
              </td>
              <td style={{
            ...td,
            width: "150px",
            whiteSpace: "nowrap"
          }}>
                {l.canCorrect === false ? <span style={{
              color: "#8C8C8C",
              fontSize: "0.92em"
            }} title={"Allocation works by subtracting this line's share of the " + "figure, and that share was never recorded. Re-upload the months " + "to rebuild it."}>
                    re-upload to correct
                  </span> : <button disabled={busyKey === l.key} onClick={() => setRemapping(l)} title="This is the wrong product - pick the right one, and remember it">
                      Allocate
                    </button>}
              </td>
            </tr>)}
          {!data.lines.length && <tr><td style={{
            ...td,
            color: "#888"
          }}>
              {data.totalUnits > 0 ? "This figure did not come from a PMR upload, so there is no match to check." : "No usage recorded against this product."}
            </td></tr>}
        </tbody>
      </table>

      {remapping && <RemapDialog line={remapping} onCancel={() => setRemapping(null)} onPick={c => apply(remapping, c.vppid)} />}
    </div>;
};
export default PmrMatchLines;
